import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { fetchGoogleReviews } from "@/utils/googleReviews";

// ─── helpers ────────────────────────────────────────────────────────────

/** "2025-12-05T14:59:01.742Z" → "December 2025". Safe on bad input. */
function formatMonthYear(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

/** numeric rating → 5 discrete star states (amber-filled vs muted). */
function ratingToFilledCount(rating) {
  if (rating === null || rating === undefined || rating === "") return 5;
  const raw = Number(rating);
  const n = Math.round(Number.isFinite(raw) ? raw : 5);
  return Math.min(5, Math.max(0, n));
}

// ─── atoms ──────────────────────────────────────────────────────────────

function Stars({ rating, size = 14 }) {
  const filled = ratingToFilledCount(rating);
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating || 5} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          size={size}
          className={s <= filled ? "fill-amber-400 text-amber-400" : "text-brand-light-grey"}
        />
      ))}
    </div>
  );
}

function GoogleG({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="flex-shrink-0 opacity-70">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function Avatar({ name, compact }) {
  const initial = (name || "G").trim().charAt(0).toUpperCase();
  const size = compact ? "w-7 h-7 text-xs" : "w-9 h-9 text-sm";
  return (
    <div
      className={`${size} rounded-full bg-brand-aqua-light flex items-center justify-center text-brand-aqua-dark font-heading font-bold flex-shrink-0`}
      aria-hidden="true"
    >
      {initial}
    </div>
  );
}

// ─── card ───────────────────────────────────────────────────────────────

function ReviewTile({ review, compact, showDate }) {
  const name = review.authorName || review.displayName || "Google user";
  const clamp = compact ? "line-clamp-4" : "line-clamp-5";
  const pad = compact ? "p-4" : "p-5 sm:p-6";
  const textSize = compact ? "text-sm" : "text-[15px]";
  const dateLabel = showDate ? formatMonthYear(review.publishTime) : "";

  return (
    <div
      className={`flex flex-col ${pad} rounded-2xl border border-brand-light-grey bg-white h-full transition-colors hover:border-brand-aqua/40`}
    >
      <div className="flex items-center justify-between mb-3">
        <Stars rating={review.rating} size={compact ? 12 : 14} />
        <GoogleG size={compact ? 12 : 14} />
      </div>

      <p className={`${textSize} text-brand-grey leading-relaxed ${clamp} flex-1`}>
        {review.text || ""}
      </p>

      <div className="mt-4 pt-4 border-t border-brand-light-grey/60 flex items-center gap-3">
        <Avatar name={name} compact={compact} />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-brand-black truncate">{name}</p>
          {dateLabel && <p className="text-[11px] text-brand-grey">{dateLabel}</p>}
        </div>
      </div>
    </div>
  );
}

// ─── skeleton ───────────────────────────────────────────────────────────

function TileSkeleton({ compact }) {
  const pad = compact ? "p-4" : "p-5 sm:p-6";
  const h = compact ? "h-36" : "h-48";
  return (
    <div className={`rounded-2xl border border-brand-light-grey bg-white ${pad} ${h} animate-pulse`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={s} className="w-3 h-3 rounded-sm bg-brand-light-grey" />
          ))}
        </div>
        <div className="w-3.5 h-3.5 rounded-full bg-brand-light-grey" />
      </div>
      <div className="space-y-2">
        <div className="w-full h-2.5 rounded bg-brand-light-grey" />
        <div className="w-11/12 h-2.5 rounded bg-brand-light-grey" />
        <div className="w-2/3 h-2.5 rounded bg-brand-light-grey" />
      </div>
      <div className="mt-auto pt-4 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-brand-light-grey" />
        <div className="flex-1 space-y-1.5">
          <div className="w-24 h-2.5 rounded bg-brand-light-grey" />
          <div className="w-16 h-2 rounded bg-brand-light-grey" />
        </div>
      </div>
    </div>
  );
}

// ─── main component ─────────────────────────────────────────────────────

/**
 * Premium tiled display of live Google reviews, designed to feel native to
 * the site (not a third-party widget).
 *
 * Consumes /api/google-reviews (backend cached 24h, frontend module-cached
 * per session → one network call per session regardless of how many
 * instances are mounted).
 *
 * @param {number}  limit          Max reviews shown. Default 3.
 * @param {boolean} compact        Tighter padding + shorter clamp. Default false.
 * @param {boolean} showDate       Show "Month Year" below name. Default true.
 * @param {boolean} showHeading    Render a small lead-in heading above tiles. Default false.
 * @param {string}  className
 * @param {string}  testId
 */
export default function GoogleReviewTiles({
  limit = 3,
  compact = false,
  showDate = true,
  showHeading = false,
  className = "",
  testId = "google-review-tiles",
}) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchGoogleReviews().then((d) => {
      if (cancelled) return;
      setData(d);
      setLoaded(true);
    });
    return () => { cancelled = true; };
  }, []);

  const reviews = Array.isArray(data?.reviews) ? data.reviews.slice(0, limit) : [];
  const gridCols = limit >= 3
    ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
    : limit === 2
    ? "grid-cols-1 md:grid-cols-2"
    : "grid-cols-1";

  // Loading — preserve height to avoid layout shift.
  if (!loaded) {
    return (
      <div className={className} data-testid={`${testId}-loading`}>
        {showHeading && (
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-grey mb-4 text-center">
            What our customers say
          </p>
        )}
        <div className={`grid ${gridCols} gap-4`}>
          {Array.from({ length: limit }).map((_, i) => (
            // Skeleton placeholders have no inherent identity; index-based
            // keys are correct here (list length is fixed, no reordering).
            // eslint-disable-next-line react/no-array-index-key
            <TileSkeleton key={`skeleton-${i}`} compact={compact} />
          ))}
        </div>
      </div>
    );
  }

  // Loaded but no reviews (API unconfigured / empty) — render nothing so
  // callers can display their own fallback copy/CTA.
  if (reviews.length === 0) return null;

  return (
    <div className={className} data-testid={testId}>
      {showHeading && (
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-grey mb-4 text-center">
          What our customers say
        </p>
      )}
      <div className={`grid ${gridCols} gap-4 items-stretch`}>
        {reviews.map((review, i) => (
          <ReviewTile
            key={`${review.authorName || "g"}-${review.publishTime || i}`}
            review={review}
            compact={compact}
            showDate={showDate}
          />
        ))}
      </div>
    </div>
  );
}
